import { css, CSSObject } from '@emotion/react'
import { AssetReference } from '@opallabs/cassini'
import { fonts, fontSizes, fontWeights } from '@opallabs/pulsar'
import getAssetOrientation from '../../../../helpers/functions/getAssetOrientation'

export const blueskyColors = {
  darkText: '#0B0F14',
  greyText: '#42576C',
  linkBorder: '1px solid #D4DBE2',
}

const MAX_MEDIA_WIDTH = '410px'
const MAX_MEDIA_HEIGHT = '307px'
const MEDIA_RADIUS = '12px'

export const headerWrapper = css({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  margin: '0 10px',
})

// Passed to AccountName's `style` prop, which spreads it into a css() call,
// so this must remain a plain object rather than a css() result.
export const accountName: CSSObject = {
  fontWeight: 'bolder',
  fontSize: '16px',
  margin: '0 0 0 10px',
  maxWidth: '180px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const accountHandle = css({
  color: blueskyColors.greyText,
  fontSize: '15px',
  margin: '0 0 0 5px',
  maxWidth: '175px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const contentWrapper = css({
  margin: '10px 10px 0 10px',
})

// Forwarded to ContentTextInput's `style` prop (kuiper styleOverrides), so
// this stays a plain object rather than a css() result.
export const contentInput: CSSObject = {
  margin: '7px 7px 0 7px',
  fontSize: '15px',
  fontWeight: 500,
  lineHeight: '18px',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}

const LINK_ASSET_HEIGHT = '215px'

export const getLinkAssetStyle = (hasAsset: boolean): CSSObject => ({
  border: blueskyColors.linkBorder,
  borderBottom: 'none',
  borderRadius: `${MEDIA_RADIUS} ${MEDIA_RADIUS} 0 0`,
  height: hasAsset ? LINK_ASSET_HEIGHT : 0,
  width: MAX_MEDIA_WIDTH,
  img: {
    height: LINK_ASSET_HEIGHT,
  },
})

export const linkTitleStyle: CSSObject = {
  color: blueskyColors.darkText,
  fontFamily: fonts.helvetica,
  fontSize: fontSizes.large,
  fontWeight: fontWeights.medium,
  padding: '2.5px 5px',
}

export const linkDomainStyle: CSSObject = {
  borderTop: blueskyColors.linkBorder,
  color: blueskyColors.greyText,
  fontFamily: fonts.helvetica,
  fontSize: fontSizes.medium,
  marginTop: '2.5px',
  padding: '5px 5px 2.5px 5px',
}

export const getLinkStyle = (hasAsset: boolean): CSSObject => ({
  border: blueskyColors.linkBorder,
  borderRadius: hasAsset ? `0 0 ${MEDIA_RADIUS} ${MEDIA_RADIUS}` : MEDIA_RADIUS,
  borderTop: 'none',
  padding: '10px',
  '.kuiper-input.kuiper-filled .kuiper-input-container, .kuiper-input.kuiper-focused .kuiper-input-container':
    {
      backgroundColor: 'white',
    },
})

export const galleryContainer: CSSObject = {
  width: MAX_MEDIA_WIDTH,
  overflow: 'auto',

  '> div': {
    columnGap: '8px',
    position: 'relative',
  },
  '.kuiper-asset': {
    backgroundColor: 'white',
    'img,video': {
      borderRadius: MEDIA_RADIUS,
      height: '100%',
      objectFit: 'cover',
      width: '100%',
    },
  },
}

export const getAssetStyles = (
  asset: AssetReference
): { asset: CSSObject; assetBase: CSSObject } => {
  const orientation = getAssetOrientation(asset)
  return {
    assetBase: {
      overflow: 'hidden',
      border: '1px solid #ffffff',
      width: 'fit-content',
    },
    asset: {
      backgroundColor: 'white',
      width: orientation === 'portrait' ? 'auto' : MAX_MEDIA_WIDTH,
      height: orientation === 'landscape' ? 'auto' : MAX_MEDIA_HEIGHT,
      maxWidth: orientation === 'portrait' ? MAX_MEDIA_WIDTH : '',
      'img,video': {
        borderRadius: MEDIA_RADIUS,
        width: orientation !== 'portrait' ? MAX_MEDIA_WIDTH : '',
        height: orientation !== 'landscape' ? MAX_MEDIA_HEIGHT : '',
        marginBottom: '-2px',
      },
    },
  }
}
